import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import debounce from 'lodash.debounce';
import loadable from '@loadable/component';

import { Search as SearchInput } from '@prisjakt/ui-inputs';
import StoreLogo from '@prisjakt/ui-store-logo';
import { bemGenerator } from '@prisjakt/ui-base';
import { AppContext } from '@prisjakt/ui-app-context';

import { graphql } from '../../../globals/api/requests';
import SearchResultList from './components/search-result-list';
import ErrorBoundary from '../../../components/error-boundary';

import sotySearchNomineesQuery from './query.graphql';

import './index.scss';

const SotyVotePopup = loadable(() =>
  import(/* webpackChunkName: "shop-of-the-year" */ '../vote-popup')
);

const styles = bemGenerator('search-soty-nominees', [
  'results',
  'result-item',
  'wrapper',
]);

class SotySearchNominees extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      query: '',
      result: [],
      loading: false,
      selectedItem: null,
    };
    this.searchDebounced = debounce(
      query => this.search(query),
      props.debounce,
      {
        leading: true,
      }
    );
  }

  prepareSearch(query, clearResult = false) {
    const newState = { query, loading: true };
    if (clearResult) {
      newState.result = [];
    }
    this.setState(newState);
    this.searchDebounced(query);
  }

  search(query) {
    if (query.length < 2) {
      this.setState({ result: [], loading: false });
    } else {
      graphql(sotySearchNomineesQuery, {
        year: this.props.year,
        query,
      }).then(({ data }) =>
        this.setState({
          loading: false,
          result:
            data && data.shopOfTheYear && data.shopOfTheYear.search
              ? data.shopOfTheYear.search
              : [],
        })
      );
    }
  }

  render() {
    const { query, result, selectedItem } = this.state;
    const { t } = this.context;
    return (
      <React.Fragment>
        {selectedItem !== null && (
          <SotyVotePopup
            year={this.props.year}
            shop={selectedItem.store}
            nominationCategory={selectedItem.category}
            onRequestClose={() => this.setState({ selectedItem: null })}
          />
        )}
        <section className={styles.block}>
          <div className={styles.wrapper}>
            <SearchInput
              hasResults={query.length >= 2}
              onChange={str => this.prepareSearch(str)}
              onSearch={str => this.prepareSearch(str, true)}
              placeholder={t('campaigns', 'shop-of-the-year.search')}
            />
            {query.length >= 2 && (
              <SearchResultList
                className={styles.results}
                connectedToInput
                result={result.slice(0, 5)}
                showEmptyMessage={query.length >= 2}
                emptyMessage={t(
                  'campaigns',
                  'shop-of-the-year.search-no-result',
                  { query }
                )}
                isLoading={this.state.loading}
                getResultKey={item => item.store.id}
                formatResult={item => (
                  <div className={styles['result-item']}>
                    <StoreLogo
                      name={item.store.name}
                      storeId={item.store.id}
                      featured={item.store.featured}
                      hasImage={item.store.hasLogo}
                    />
                    <span>{item.store.name}</span>
                    <small>{item.category.name}</small>
                  </div>
                )}
                onSelect={item => this.setState({ selectedItem: item })}
              />
            )}
          </div>
        </section>
      </React.Fragment>
    );
  }
}

SotySearchNominees.propTypes = {
  debounce: PropTypes.number,
  year: PropTypes.number,
};

SotySearchNominees.defaultProps = {
  debounce: 250,
  year: new Date().getFullYear(),
};

SotySearchNominees.contextType = AppContext;

const SotySearchNomineesWithErrorBoundary = props => (
  <ErrorBoundary onError={() => null}>
    <SotySearchNominees {...props} />
  </ErrorBoundary>
);

export default SotySearchNomineesWithErrorBoundary;
